import { useRef, useState } from 'react';
import { DATE_FORMATS, formatDateTime, formatMoney } from '@belong/common';
import { IconLeftCaret, IconRightCaret } from '@belong/icons';
import { Text } from '@belong/ui';
import clsx from 'classnames';
import { formatString } from 'strings';
import styles from './comparables-step-list.module.css';

type ComparablesStepListProps = {
  initialEstimate?: any;
  lowerEstimates?: any;
  higherEstimates?: any;
  homeMediaValue?: any;
  address?: any;
  unit?: any;
  formatAddress: any;
};

export function ComparablesStepList({
  initialEstimate,
  higherEstimates,
  lowerEstimates,
  homeMediaValue,
  address,
  unit,
  formatAddress,
}: ComparablesStepListProps) {
  const tableRef = useRef<HTMLDivElement>(null);
  const [refPosition, setRefPosition] = useState(0);

  const scroll = (e: any, scrollOffset: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.cancelable && tableRef.current) {
      tableRef.current.scrollLeft += scrollOffset;
      setRefPosition(tableRef.current.scrollLeft);
    }
  };

  return (
    <div className="relative flex w-full items-center justify-center">
      <div
        className={clsx(
          'absolute z-10 flex justify-between md:hidden',
          styles.arrowsBox,
        )}
      >
        <div
          className={clsx(
            'flex h-xl w-xl items-center justify-center rounded-full bg-navy',
            refPosition === 0 ? 'bg-dark-gray' : 'bg-navy',
          )}
        >
          <IconLeftCaret
            onClick={(event) => scroll(event, -80)}
            className="text-white"
            height={20}
          />
        </div>
        <div
          className={clsx(
            'flex h-xl w-xl items-center justify-center rounded-full bg-navy',
            refPosition >= 666 ? 'bg-dark-gray' : 'bg-navy',
          )}
        >
          <IconRightCaret
            onClick={(event) => scroll(event, 80)}
            className="text-white"
            height={20}
          />
        </div>
      </div>
      <div
        ref={tableRef}
        className={clsx(
          'justify-left flex w-full overflow-scroll md:justify-center md:overflow-visible md:px-md',
          styles.tableContainer,
        )}
      >
        <table className="w-full border-collapse border border-solid border-gray">
          <thead>
            <tr className="bg-light-gray">
              <th className="border border-solid border-gray px-lg text-left">
                Price on
                <br />
                {formatDateTime({
                  dateTime: initialEstimate?.pricingEstimate?.publishedOn,
                  format: DATE_FORMATS.STANDARD_NO_YEAR,
                })}
              </th>
              <th className="border border-solid border-gray px-lg text-left">
                Sqft
              </th>
              <th className="border border-solid border-gray px-lg text-left">
                Price/
                <br />
                Sqft
              </th>
              <th className="border border-solid border-gray px-lg text-left">
                Bed
              </th>
              <th className="border border-solid border-gray px-lg text-left">
                Bath
              </th>
              <th className="border border-solid border-gray px-lg text-left">
                Price/
                <br />
                Bedroom
              </th>
              <th className="w-1/3 border border-solid border-gray px-lg text-left">
                Address
              </th>
              <th className="border border-solid border-gray px-lg text-left">
                Days
                <br />
                Listed
              </th>
            </tr>
          </thead>
          <tbody>
            {lowerEstimates.map((comparable: any) => (
              <TableRow
                key={comparable.id}
                comparable={comparable}
                formatAddress={formatAddress}
              />
            ))}
            <tr className={clsx('mainRow')}>
              <td className="border border-solid border-gray px-lg">
                <Text fontWeight="semibold">{formatMoney(homeMediaValue)}</Text>
              </td>
              <td className="border border-solid border-gray px-lg">
                <Text fontWeight="semibold">
                  {initialEstimate?.home?.areaInSquareFeet || '-'}
                </Text>
              </td>
              <td className="border border-solid border-gray px-lg">
                <Text fontWeight="semibold">
                  {formatMoney(
                    homeMediaValue /
                      (initialEstimate?.home?.areaInSquareFeet || 1),
                  )}
                </Text>
              </td>
              <td className="border border-solid border-gray px-lg">
                <Text fontWeight="semibold">
                  {initialEstimate?.home?.bedroomCount || '-'}
                </Text>
              </td>
              <td className="border border-solid border-gray px-lg">
                <Text fontWeight="semibold">
                  {initialEstimate?.home?.fullBathroomCount || '-'}
                </Text>
              </td>
              <td className="border border-solid border-gray px-lg">
                <Text fontWeight="semibold">
                  {formatMoney(
                    homeMediaValue / (initialEstimate?.home?.bedroomCount || 1),
                  )}
                </Text>
              </td>
              <td className="border border-solid border-gray px-lg">
                <Text fontWeight="semibold">
                  {formatString(address) ||
                    formatAddress(unit?.basicInfo?.address)}
                </Text>
              </td>
              <td className="border border-solid border-gray px-lg">
                <Text fontWeight="semibold">Your Home</Text>
              </td>
            </tr>
            {higherEstimates.map((comparable: any) => (
              <TableRow
                key={comparable.id}
                comparable={comparable}
                formatAddress={formatAddress}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

type HigherEstimatesRowProps = {
  comparable: any;
  formatAddress: any;
};

function TableRow({ comparable, formatAddress }: HigherEstimatesRowProps) {
  const externalListing = comparable.relatedExternalListing;

  return (
    <tr key={externalListing.id}>
      <td className="border border-solid border-gray px-lg">
        <Text>{formatMoney(externalListing.rentAmount)}</Text>
      </td>
      <td className="border border-solid border-gray px-lg">
        {externalListing.areaInSquareFeet || '-'}
      </td>
      <td className="border border-solid border-gray px-lg">
        <Text>
          {formatMoney(
            externalListing.rentAmount / externalListing.areaInSquareFeet,
          ) || '-'}
        </Text>
      </td>
      <td className="border border-solid border-gray px-lg">
        {externalListing.bedroomCount || '-'}
      </td>
      <td className="border border-solid border-gray px-lg">
        {externalListing.fullBathroomCount || '-'}
      </td>
      <td className="border border-solid border-gray px-lg">
        <Text>
          {formatMoney(
            externalListing.rentAmount / externalListing.bedroomCount,
          ) || '-'}
        </Text>
      </td>
      <td className="border border-solid border-gray px-lg">
        {formatAddress(externalListing.address)}
      </td>
      <td className="border border-solid border-gray px-lg">
        {externalListing.daysListed}
      </td>
    </tr>
  );
}
